/**
 * Cart-drawer discount-code validator.
 *
 * Goal: replace Shopify's vague "discount code isn't valid" with specific,
 * actionable messages ("expired on March 12", "add $18 more", "for signed-in
 * customers only", etc.). Works on standard Shopify (no Plus required) by
 * validating in the cart drawer BEFORE the customer hits checkout.
 *
 * IMPORTANT: all listeners are DELEGATED on document. The widget sits inside a
 * data-merge region whose innerHTML is replaced on every AJAX cart update, so
 * any listener bound directly to the toggle/form dies after the first cart
 * change (this is exactly the "button stopped clicking" bug). Delegation
 * survives every merge and also covers multiple instances (drawer + cart page).
 *
 * Flow on Apply click:
 *   1. POST the code to Shopify's discount endpoint (sets cookie).
 *   2. Re-fetch /cart.js and check if it landed in cart.discount_applications.
 *   3. If yes → success state, refresh drawer.
 *   4. If no → fetch admin.petraaroma.com /api/discount-public/info/CODE
 *      for the rules, and diagnose the failure against current cart state.
 */
(function () {
  'use strict';

  function setMsg(root, text, level) {
    const msg = root.querySelector('[data-cd-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.dataset.level = level || '';
  }

  function fmtMoney(amount) {
    const n = Number(amount || 0);
    if (Number.isInteger(n)) return `$${n}`;
    return `$${n.toFixed(2)}`;
  }

  function fmtDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    return r.json();
  }

  async function applyToShopify(code) {
    // Hitting /discount/CODE sets a session cookie; the redirect target
    // doesn't matter (we read the cart fresh after).
    await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, {
      credentials: 'same-origin'
    }).catch(() => null);
  }

  async function fetchInfo(apiBase, code) {
    try {
      const r = await fetch(`${apiBase}/api/discount-public/info/${encodeURIComponent(code)}`);
      if (!r.ok) return null;
      return await r.json();
    } catch (e) { return null; }
  }

  function applied(cart, code) {
    const ups = (code || '').trim().toUpperCase();
    // cart.discount_codes covers free-shipping codes (which don't always show
    // in discount_applications until checkout). Each entry has { code, amount,
    // type: "fixed_amount" | "percentage" | "shipping", applicable: bool }.
    const codeEntry = (cart.discount_codes || []).find(d =>
      String(d.code || '').toUpperCase() === ups
    );
    if (codeEntry && codeEntry.applicable === true) return true;
    if (codeEntry && codeEntry.applicable === false) return false; // explicitly rejected → diagnose
    // Amount-off / order discounts also show in discount_applications.
    return (cart.discount_applications || []).some(d =>
      d.type === 'discount_code' && String(d.title || d.code || '').toUpperCase() === ups
    );
  }

  function diagnose(info, cart, code, isLoggedIn) {
    const now = new Date();
    if (!info || !info.found) {
      return `We can't find a code called "${code}". Double-check the spelling.`;
    }
    if (info.status && info.status !== 'ACTIVE') {
      if (info.starts_at && new Date(info.starts_at) > now) {
        return `This code becomes active on ${fmtDate(info.starts_at)}.`;
      }
      if (info.ends_at && new Date(info.ends_at) < now) {
        return `This code expired on ${fmtDate(info.ends_at)}.`;
      }
      return `This code isn't active right now. Contact us if you think it should work.`;
    }
    if (info.ends_at && new Date(info.ends_at) < now) {
      return `This code expired on ${fmtDate(info.ends_at)}.`;
    }
    if (info.starts_at && new Date(info.starts_at) > now) {
      return `This code becomes active on ${fmtDate(info.starts_at)}.`;
    }
    // Min subtotal — proactive "add $X more"
    if (info.min_subtotal && cart) {
      const subtotal = (cart.items_subtotal_price ?? cart.total_price ?? 0) / 100;
      if (subtotal < info.min_subtotal) {
        const need = (info.min_subtotal - subtotal).toFixed(2);
        return `This code needs ${fmtMoney(info.min_subtotal)}+ in your cart. Add ${fmtMoney(need)} more to unlock it.`;
      }
    }
    if (info.min_quantity && cart) {
      const qty = (cart.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
      if (qty < info.min_quantity) {
        return `This code needs at least ${info.min_quantity} item${info.min_quantity === 1 ? '' : 's'} in your cart.`;
      }
    }
    // Customer segment / specific-customer eligibility
    if (info.customer_eligibility === 'segments' && !isLoggedIn) {
      return `This code is for signed-in customers — sign in to your account to use it.`;
    }
    if (info.customer_eligibility === 'specific_customers' && !isLoggedIn) {
      return `This code is reserved for specific customers — sign in to your account to check eligibility.`;
    }
    if (info.customer_eligibility === 'segments' && isLoggedIn) {
      const segs = (info.customer_segment_names || []).join(', ');
      return `This code is for a specific customer group${segs ? ` (${segs})` : ''} and your account isn't in it.`;
    }
    // Already used (one-per-customer)
    if (info.applies_once_per_customer && isLoggedIn) {
      return `Looks like you've already used this code on a previous order.`;
    }
    // Combinations conflict — if cart already has a discount and this one doesn't combine
    if (cart && (cart.discount_applications || []).length > 0 && info.combines_with) {
      const c = info.combines_with;
      if (info.type === 'free_shipping' && !c.shipping) {
        return `This code can't combine with the discount already applied. Remove the other code to use it.`;
      }
      if (info.type === 'amount_off' && !c.order && !c.product) {
        return `This code can't combine with the discount already applied. Remove the other code to use it.`;
      }
    }
    // Product restriction — Shopify doesn't expose product eligibility cleanly, so
    // we surface a general explanation rather than guess.
    if (info.type === 'amount_off' || info.type === 'free_shipping') {
      return `This code can't be applied to the items currently in your cart. It may be limited to specific products or excluded items may be present.`;
    }
    return `This code can't be applied to your cart right now. Contact us if you think it should work.`;
  }

  function refreshCartDrawer() {
    // Re-fetch the drawer section to reflect the applied discount.
    if (typeof window.refreshCartDrawer === 'function') {
      window.refreshCartDrawer();
      return;
    }
    // Fallback: dispatch a generic event a few common drawers listen for.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function handleApply(root) {
    const apiBase = root.dataset.apiBase || 'https://admin.petraaroma.com';
    const isLoggedIn = root.dataset.customerLoggedIn === 'true';
    const input = root.querySelector('[data-cd-code]');
    const applyBtn = root.querySelector('[data-cd-apply]');
    if (!input || !applyBtn) return;

    const code = (input.value || '').trim().toUpperCase();
    if (!code) return;
    applyBtn.disabled = true;
    const originalLabel = applyBtn.textContent;
    applyBtn.textContent = 'Applying…';
    setMsg(root, '', '');

    try {
      // 1. Pre-validate against the discount's rules BEFORE asking Shopify to apply.
      // Shopify's cart-level "applicable" flag is too permissive for free-shipping
      // codes (it marks the code as applicable even when the min subtotal isn't met
      // — the min only enforces at checkout). So we validate locally first.
      const [cart, info] = await Promise.all([fetchCart(), fetchInfo(apiBase, code)]);
      const preError = diagnose(info, cart, code, isLoggedIn);

      // diagnose returns a fallback message for "unknown reason"; only treat
      // it as a real validation failure if it returns one of the SPECIFIC reasons.
      const hasBlockingRule = info && info.found && (
        (info.status && info.status !== 'ACTIVE') ||
        (info.ends_at && new Date(info.ends_at) < new Date()) ||
        (info.starts_at && new Date(info.starts_at) > new Date()) ||
        (info.min_subtotal && ((cart.items_subtotal_price ?? cart.total_price ?? 0) / 100) < info.min_subtotal) ||
        (info.min_quantity && ((cart.items || []).reduce((s, i) => s + (i.quantity || 0), 0)) < info.min_quantity) ||
        (info.customer_eligibility === 'segments' && !isLoggedIn) ||
        (info.customer_eligibility === 'specific_customers' && !isLoggedIn)
      );

      if (!info || !info.found) {
        setMsg(root, preError, 'error');
        return;
      }
      if (hasBlockingRule) {
        setMsg(root, preError, 'error');
        return;
      }

      // 2. Rules look OK — actually apply via Shopify.
      await applyToShopify(code);
      const newCart = await fetchCart();
      if (applied(newCart, code)) {
        setMsg(root, `✓ Code applied — ${code}`, 'success');
        refreshCartDrawer();
      } else {
        // Shopify rejected it for a reason we couldn't pre-validate (likely a
        // product/collection restriction or a one-per-customer + history check).
        setMsg(root, diagnose(info, newCart, code, isLoggedIn), 'error');
      }
    } catch (err) {
      console.warn('discount apply failed', err);
      setMsg(root, `Something went wrong applying that code. Try again or contact us.`, 'error');
    } finally {
      applyBtn.disabled = false;
      applyBtn.textContent = originalLabel;
    }
  }

  // Delegated toggle — survives cart merges replacing the widget's DOM.
  document.addEventListener('click', function (e) {
    const toggle = e.target.closest('[data-cd-toggle]');
    if (!toggle) return;
    const root = toggle.closest('[data-cart-discount]');
    if (!root) return;
    const body = root.querySelector('[data-cd-body]');
    const input = root.querySelector('[data-cd-code]');
    if (!body) return;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    body.hidden = expanded;
    if (!expanded && input) setTimeout(() => input.focus(), 50);
  });

  // Delegated submit for the same reason.
  document.addEventListener('submit', function (e) {
    const form = e.target.closest('[data-cd-form]');
    if (!form) return;
    e.preventDefault();
    const root = form.closest('[data-cart-discount]');
    if (root) handleApply(root);
  });
})();
